import { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiService from "../services/api";
import "../styles/StaffPages.css";

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

const DEFAULT_DATA = [
  { title: "Low lift \u2014 weekly-friendly, minimal staff", cls: "navy", items: [
    { id: "football_practice_viewing", name: "Football practice viewing", desc: "Fans watch a live football practice from a designated area.", tags: ["w","e","b"], ops: "Rope off area. No coach interaction. 15 min setup." },
    { id: "mbb_practice_viewing", name: "MBB practice viewing", desc: "Fans watch men\u2019s basketball practice from arena seating. Intimate setting.", tags: ["w","e","b"], ops: "Arena upper deck. Smaller venue = more exclusive." },
    { id: "closed_door_practice_access", name: "Closed-door practice access", desc: "Exclusive access to a practice normally closed to the public.", tags: ["w","e"], ops: "Same setup as normal viewing. \u2018Closed door\u2019 label makes it feel rare." },
    { id: "olympic_sports_training_session", name: "Olympic sports training session", desc: "Watch volleyball, soccer, swimming, or track practice. Rotates weekly.", tags: ["w","b"], ops: "Coach gives 2-min intro. Fans observe from stands." },
    { id: "stadium_arena_tour", name: "Stadium / arena tour", desc: "Guided walkthrough \u2014 press box, locker room hallway, field level, suites.", tags: ["e","b"], ops: "Student worker leads. 45-60 min. No game day required." },
    { id: "film_room_friday", name: "Film Room Friday", desc: "Sit in on a film breakdown session with a GA or analyst. In-person or virtual.", tags: ["b"], ops: "GA hosts. 30 min. Virtual or in-person." },
    { id: "walk_through_weights_session", name: "Walk through a weights session", desc: "Observe a team strength & conditioning session from the viewing gallery.", tags: ["w","b"], ops: "Most weight rooms have a viewing area. Zero athlete interaction." },
    { id: "exclusive_gear_bag_drop", name: "Exclusive gear bag drop", desc: "Curated bag of team gear (hat, shirt, sticker pack, koozie).", tags: ["w","b"], ops: "Pre-pack in bulk. Ship or pickup at team store." },
    { id: "social_media_shoutout", name: "Social media shoutout", desc: "Fan featured on the official team social account.", tags: ["w","e"], ops: "Social team template. 5 min per post. Zero cost." },
    { id: "personalized_video_from_player", name: "Personalized video from a player", desc: "Athlete records a 30-sec personalized video for the fan.", tags: ["w","b"], ops: "Athlete records on phone. Batch 3-5 after practice. SID reviews." },
    { id: "parking_pass_premium_lot", name: "Parking pass (premium lot)", desc: "Reserved spot in a premium lot on gameday.", tags: ["w","b"], ops: "Digital pass via app. No staff needed day-of." },
    { id: "player_workout_challenge", name: "Player workout challenge", desc: "Athlete designs a workout. Fans do it, post times. Top performers win gear.", tags: ["w","b"], ops: "Athlete films demo. Fans self-report. Zero game-day lift." },
    { id: "athlete_led_camps_or_workouts", name: "Athlete-led camps or workouts", desc: "Open sessions led by current athletes \u2014 skills clinics, group workouts.", tags: ["b"], ops: "Offseason. Athletes lead, staff supervises. 60-90 min." },
    { id: "custom_video_from_coach", name: "Custom video from a coach", desc: "Position coach records a 30-sec personalized video for the fan.", tags: ["w","e"], ops: "Coach records on phone. Batch 3-4 after practice." },
  ]},
  { title: "Medium lift \u2014 gameday or monthly, some coordination", cls: "teal", items: [
    { id: "pregame_field_pass", name: "Pregame field pass", desc: "Walk the field 90 min before kickoff. Warmups up close. Photo ops.", tags: ["w","b"], ops: "Escort to field. 30-45 min. Security walks them off." },
    { id: "mbb_sideline_passes_warmups", name: "MBB sideline passes (warmups)", desc: "Courtside during pre-game shootaround. Leave before tip-off.", tags: ["w","b"], ops: "Courtside chairs 30 min before. Escorted off 5 min before tip." },
    { id: "watch_warmups_sideline_football", name: "Watch warmups from the sideline (football)", desc: "Stand on the sideline during pre-game warmups. You\u2019re WITH the team.", tags: ["w","b"], ops: "Wristband. Security escort. Off field 15 min before kick." },
    { id: "warm_up_tunnel_watch", name: "Warm-up tunnel watch", desc: "Stand in the tunnel and watch the team run out for warmups.", tags: ["b"], ops: "Position fans 20 min before. Quick in, quick out." },
    { id: "post_game_locker_room_walkthrough", name: "Post-game locker room walkthrough", desc: "Walk through the locker room after the game. Still set up, no players.", tags: ["w","b"], ops: "30 min after game. Players gone. Equipment allows walkthrough." },
    { id: "post_game_locker_room_big_win", name: "Post-game locker room after a big win", desc: "Enter while the energy is live. Confetti, music, celebration vibes.", tags: ["w"], ops: "Big wins only. Coach/SID approves. Fans stay 5-10 min." },
    { id: "livestream_postgame_locker_room", name: "Livestream: postgame locker room hype", desc: "Exclusive livestream of coach\u2019s postgame speech and celebration.", tags: ["w","b"], ops: "Tripod + phone. Coach approves. 3-5 min stream." },
    { id: "reserved_premium_tailgate_spot", name: "Reserved premium tailgate spot", desc: "Guaranteed reserved spot with table in a premium tailgate area.", tags: ["b"], ops: "Mark spots morning-of. Table + tent optional upsell." },
    { id: "tent_or_annex_access", name: "Tent or annex access", desc: "Access to a premium hospitality tent or annex area pre-game.", tags: ["w","b"], ops: "Existing hospitality infrastructure. Wristband check-in." },
    { id: "high_five_tunnel_kids", name: "High-five tunnel (kids)", desc: "Kids line the tunnel and high-five players as they run out.", tags: ["w","b"], ops: "Waiver signed. Kids line up 10 min before. Done in 2 min." },
    { id: "ball_boy_ball_girl_experience", name: "Ball boy / ball girl experience (kids)", desc: "Kids serve as ball boys/girls during warmups or a quarter.", tags: ["w","b"], ops: "Equipment provides jerseys. Kids briefed 30 min before." },
    { id: "pregame_hosting_meal", name: "Pregame hosting meal", desc: "Small group meal in a premium area before the game. Catered.", tags: ["e","b"], ops: "Book suite or premium room. Catering handles food." },
    { id: "pre_game_team_walk_through_viewing", name: "Pre-game team walk-through viewing", desc: "Observe the team\u2019s pre-game walk-through at the stadium.", tags: ["w","e"], ops: "Fans in upper deck. Silent observation. 20-30 min." },
    { id: "post_game_press_conference_viewing", name: "Post-game press conference viewing", desc: "Sit in the press room and watch the coach\u2019s post-game presser.", tags: ["w","e"], ops: "Fans in back of press room. Silent observation." },
    { id: "team_arrival_experience", name: "Team arrival experience", desc: "Line the team\u2019s arrival path. High-fives, photos, energy.", tags: ["w","b"], ops: "VIP section of walk path. Fans check in 90 min before." },
    { id: "courtside_field_level_seats", name: "Courtside or field-level seats for a game", desc: "Premium seat upgrade \u2014 courtside MBB or front rows football.", tags: ["w","e","b"], ops: "Ticket office identifies available inventory per game." },
    { id: "sit_with_former_players_alums", name: "Sit with former players / alums in VIP", desc: "Watch the game alongside legends in a VIP section.", tags: ["w","b"], ops: "Coordinate with alumni relations. Reserved section." },
    { id: "press_box_sideline_media_credential", name: "Press box or sideline media credential", desc: "Limited media-style credential. Press box or sideline access.", tags: ["w","b"], ops: "SID issues credential. Fan briefed on rules." },
    { id: "name_on_the_jumbotron", name: "Name on the jumbotron", desc: "Fan\u2019s name and custom message on the video board during a game.", tags: ["w","b"], ops: "Graphics template. Fan submits message. 15 sec display." },
    { id: "help_athletes_pick_warmup_music", name: "Help athletes pick warmup music", desc: "Fans vote on or curate the pre-game warmup playlist.", tags: ["e"], ops: "Playlist submitted via app. Equipment loads it." },
  ]},
  { title: "High value \u2014 limited availability, creates FOMO", cls: "purple", items: [
    { id: "lead_team_out_of_tunnel", name: "Lead the team out of the tunnel", desc: "Run out of the tunnel right behind the team during player intros.", tags: ["w"], ops: "Fan briefed 45 min before. Security escort." },
    { id: "fan_honoree_of_the_game", name: "Fan honoree of the game", desc: "Recognized on the field/court. Name on video board, PA announcement.", tags: ["e","w"], ops: "Script with PA. Fan escorted to field. 2-min ceremony." },
    { id: "flip_coin_deliver_game_ball", name: "Flip the coin or deliver the game ball", desc: "Walk to midfield for the coin toss or deliver the ball to the ref.", tags: ["e"], ops: "Game ops sign-off. Fan briefed. 1-2 per season." },
    { id: "sit_on_bench_during_warmups", name: "Sit on the bench during warmups", desc: "Sit on the team bench during pre-game warmups.", tags: ["w","e"], ops: "Waiver signed. End of bench. Escorted on/off." },
    { id: "ad_box_access", name: "AD box access", desc: "Watch from the Athletic Director\u2019s suite. Food, premium seating.", tags: ["e"], ops: "AD reserves 2-4 seats per game. Will call check-in." },
    { id: "game_worn_gear_raffle", name: "Game-worn gear (raffle / giveaway)", desc: "Authenticated game-worn jersey, gloves, or cleats.", tags: ["w","e"], ops: "Equipment pulls items. Auth card included." },
    { id: "help_athletes_design_merch", name: "Help athletes design merch", desc: "Co-design a limited merch drop with the creative team.", tags: ["e"], ops: "Creative team runs 1-hr session. 1-2 per season." },
    { id: "team_hype_video_photo_shoot", name: "Be part of a team hype video / photo shoot", desc: "Appear in an official team video or photo shoot.", tags: ["w","e"], ops: "Scheduled around existing shoots. 30-60 min." },
    { id: "rent_the_field_or_court", name: "Rent the field or court", desc: "Private event \u2014 birthdays, pickup games, proposals.", tags: ["b"], ops: "Facilities approves. 2-hour block. Staff on-site." },
    { id: "ride_along_team_bus", name: "Ride-along in team bus or sprinter van", desc: "Ride with the team to a nearby away game.", tags: ["e"], ops: "HC/AD approval. NDA + waiver. Extremely rare." },
    { id: "one_on_one_facetime_zoom", name: "One-on-one FaceTime / Zoom with a player", desc: "15-min personal video call with an athlete.", tags: ["w","b"], ops: "Athlete + SID on call. 15 min. NIL considerations." },
    { id: "host_player_for_dinner", name: "Host a player for dinner (local fans)", desc: "Local fan hosts an athlete for a home-cooked meal.", tags: ["e"], ops: "Compliance review. Athlete volunteers. NIL considerations." },
    { id: "shadow_player_coach_staffer", name: "Shadow a player, coach, or staffer for a day", desc: "Full day behind the scenes \u2014 meetings, practice, film, meals.", tags: ["e"], ops: "HC approval. NDA. 1 per season max." },
    { id: "pa_announcer_for_a_play", name: "PA announcer for a play", desc: "Read the PA announcement for one play during the game.", tags: ["e"], ops: "Pre-screened. Rehearsed. PA team supervises." },
  ]},
  { title: "Wildcards \u2014 engagement spikes + broad appeal", cls: "coral", items: [
    { id: "airpods_max_high_value_tech", name: "AirPods Max or high-value tech (~$500)", desc: "Nothing to do with sports \u2014 broadens appeal beyond diehards.", tags: ["w","e"], ops: "Buy in bulk. Ship directly. Zero athletics coordination." },
    { id: "gift_card_bundle", name: "Gift card bundle ($200-$500)", desc: "Amazon, Nike, local restaurants, campus bookstore.", tags: ["w","e"], ops: "Digital delivery. Instant fulfillment." },
    { id: "signed_helmet_football_basketball", name: "Signed helmet / football / basketball", desc: "Coach or team-signed memorabilia. Authenticated.", tags: ["w","e"], ops: "HC signing session. Auth card from equipment." },
    { id: "season_ticket_upgrade", name: "Season ticket upgrade", desc: "Upgrade existing season ticket to a better section.", tags: ["e"], ops: "Ticket office identifies inventory. Digital swap." },
    { id: "podcast_guest_pass", name: "Podcast Guest Pass", desc: "Appear on a team podcast or content segment with an athlete.", tags: ["w","e"], ops: "Content team schedules 20-min recording." },
    { id: "team_banquet_awards_night_invite", name: "Team banquet or awards night invite", desc: "Attend the end-of-season team banquet with coaches.", tags: ["w","b"], ops: "Add seats to existing event. Annual." },
    { id: "video_game_session_with_players", name: "Video game session with players", desc: "Madden or 2K tournament with athletes. Streamed or in-person.", tags: ["w","b"], ops: "Athletes bring consoles. 60-90 min. Monthly. NIL considerations." },
    { id: "fan_vs_athlete_trivia_skills", name: "Fan vs. athlete trivia or skills challenge", desc: "Compete against athletes in trivia, free throws, or drills.", tags: ["w","b"], ops: "Monthly event. Content team films. 60 min." },
    { id: "ultimate_fan_internship", name: "Ultimate Fan Internship", desc: "1-week behind-the-scenes internship with athletics team.", tags: ["e"], ops: "1 per year. Structured schedule. Background check." },
    { id: "offseason_retreat_training_session", name: "Offseason retreat or training session", desc: "Join the team for a day during offseason conditioning.", tags: ["e"], ops: "Offseason only. Observe, don't participate. 1-2 per year." },
    { id: "travel_with_team_road_game", name: "Travel with the team to a road game", desc: "Full travel experience \u2014 bus, hotel, behind-the-scenes access.", tags: ["e"], ops: "HC/AD approval. NDA + waiver. 1 per year." },
  ]},
];

const TAG_MAP = { w: "WIN IT", e: "EARN IT", b: "BUY IT" };
const TAG_COLORS = {
  w: { bg: "rgba(0, 184, 212, 0.1)", color: "#0097A7" },
  e: { bg: "rgba(255, 152, 0, 0.1)", color: "#E65100" },
  b: { bg: "rgba(76, 175, 80, 0.1)", color: "#2E7D32" },
};
const SEC_COLORS = {
  navy: "#0C447C",
  teal: "#0F6E56",
  purple: "#534AB7",
  coral: "#993C1D",
};
const ALL_TAGS = ["w", "e", "b"];

function deepClone(obj) { return JSON.parse(JSON.stringify(obj)); }

export default function RewardSelectionPage() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [sections, setSections] = useState(() => deepClone(DEFAULT_DATA));
  const [votes, setVotes] = useState({});
  const [notes, setNotes] = useState({});
  const [noteOpen, setNoteOpen] = useState(null);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [loading, setLoading] = useState(!!schoolId);
  const [school, setSchool] = useState(null);
  const [addingTo, setAddingTo] = useState(null);
  const [newItem, setNewItem] = useState({ name: "", desc: "", ops: "" });
  const [editingItem, setEditingItem] = useState(null);
  const [editData, setEditData] = useState({ name: "", desc: "", ops: "" });
  const [dragSource, setDragSource] = useState(null); // { si, ii } — for rendering
  const [dropTarget, setDropTarget] = useState(null); // { si, ii } — for rendering
  const dragSourceRef = useRef(null);
  const dropTargetRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const loadedRef = useRef(false);

  // Load data from API
  useEffect(() => {
    if (!schoolId) { setLoading(false); return; }
    const load = async () => {
      try {
        const res = await apiService.getRewardSelections(schoolId);
        setSchool(res.school);
        const data = res.reward_selections;
        if (data) {
          if (data.catalog && data.catalog.length > 0) {
            setSections(deepClone(data.catalog));
          }
          if (data.votes) setVotes(data.votes);
          if (data.notes) setNotes(data.notes);
        }
      } catch (err) {
        console.error("Failed to load reward selections:", err);
      } finally {
        setLoading(false);
        loadedRef.current = true;
      }
    };
    load();
  }, [schoolId]);

  // Check superuser
  useEffect(() => {
    const check = async () => {
      const token = localStorage.getItem("access_token");
      if (!token) return;
      try {
        const user = await apiService.fetchCurrentUser();
        if (user?.is_superuser) setIsSuperuser(true);
      } catch {}
    };
    check();
  }, []);

  // Auto-save debounced (votes + notes for all users, sections/catalog for superusers)
  useEffect(() => {
    if (!schoolId || !loadedRef.current) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      const payload = { votes, notes };
      if (isSuperuser) payload.catalog = sections;
      apiService.saveRewardSelections(schoolId, payload).catch(err => {
        console.error("Failed to save reward selections:", err);
      });
    }, 800);
    return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
  }, [votes, notes, sections, schoolId, isSuperuser]);

  const vote = useCallback((key, val) => {
    setVotes(prev => {
      const next = { ...prev };
      if (next[key] === val) delete next[key];
      else next[key] = val;
      return next;
    });
  }, []);

  // Admin: toggle tag on an item
  const toggleTag = (si, ii, tag) => {
    setSections(prev => {
      const next = deepClone(prev);
      const tags = next[si].items[ii].tags;
      const idx = tags.indexOf(tag);
      if (idx >= 0) tags.splice(idx, 1);
      else tags.push(tag);
      return next;
    });
  };

  // Admin: add item to section
  const addItem = (si) => {
    if (!newItem.name.trim()) return;
    setSections(prev => {
      const next = deepClone(prev);
      next[si].items.push({
        id: slugify(newItem.name),
        name: newItem.name.trim(),
        desc: newItem.desc.trim(),
        tags: [],
        ops: newItem.ops.trim(),
      });
      return next;
    });
    setNewItem({ name: "", desc: "", ops: "" });
    setAddingTo(null);
  };

  // Admin: delete item
  const deleteItem = (si, ii) => {
    setSections(prev => {
      const next = deepClone(prev);
      next[si].items.splice(ii, 1);
      return next;
    });
  };

  // Admin: save edit
  const saveEdit = () => {
    if (!editingItem || !editData.name.trim()) return;
    const { si, ii } = editingItem;
    setSections(prev => {
      const next = deepClone(prev);
      next[si].items[ii].name = editData.name.trim();
      next[si].items[ii].desc = editData.desc.trim();
      next[si].items[ii].ops = editData.ops.trim();
      return next;
    });
    setEditingItem(null);
  };

  // Drag and drop (superuser only) — reorder within a section
  // Uses refs to map item index -> DOM element for position calculation
  const itemRefsMap = useRef({}); // key: "si-ii" -> DOM element

  const handleDragStart = (si, ii, e) => {
    dragSourceRef.current = { si, ii };
    setDragSource({ si, ii });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleSectionDragOver = (si, e) => {
    e.preventDefault();
    const src = dragSourceRef.current;
    if (!src || src.si !== si) return; // only reorder within same section for now
    const y = e.clientY;
    const items = sections[si].items;
    let targetIi = items.length; // default: end
    for (let i = 0; i < items.length; i++) {
      const el = itemRefsMap.current[`${si}-${i}`];
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      const mid = rect.top + rect.height / 2;
      if (y < mid) { targetIi = i; break; }
    }
    if (targetIi !== dropTargetRef.current?.ii || si !== dropTargetRef.current?.si) {
      dropTargetRef.current = { si, ii: targetIi };
      setDropTarget({ si, ii: targetIi });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const src = dragSourceRef.current;
    const tgt = dropTargetRef.current;
    if (!src || !tgt) { handleDragEnd(); return; }
    const { si: fromSi, ii: fromIi } = src;
    let { si: toSi, ii: toIi } = tgt;
    if (fromSi !== toSi) { handleDragEnd(); return; } // same section only
    if (fromIi === toIi || fromIi + 1 === toIi) { handleDragEnd(); return; } // no-op
    setSections(prev => {
      const next = deepClone(prev);
      const [item] = next[fromSi].items.splice(fromIi, 1);
      const insertAt = toIi > fromIi ? toIi - 1 : toIi;
      next[toSi].items.splice(insertAt, 0, item);
      return next;
    });
    handleDragEnd();
  };

  const handleDragEnd = () => {
    dragSourceRef.current = null;
    dropTargetRef.current = null;
    setDragSource(null);
    setDropTarget(null);
  };

  // Counts
  const total = sections.reduce((s, sec) => s + sec.items.length, 0);
  let yesCount = 0, maybeCount = 0, noCount = 0;
  Object.values(votes).forEach(v => { if (v === "yes") yesCount++; else if (v === "maybe") maybeCount++; else if (v === "no") noCount++; });

  const inputStyle = { width: "100%", padding: "8px 12px", border: "1px solid #E1E8ED", borderRadius: 8, fontSize: 13, fontFamily: "'Baikal Book', sans-serif", outline: "none" };

  if (loading) {
    return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#fff", fontFamily: "'Baikal Book', sans-serif", color: "#90A4AE" }}>Loading...</div>;
  }

  return (
    <div style={{ minHeight: "100vh", background: "#fff", fontFamily: "'Baikal Book', sans-serif" }}>
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "40px 24px 100px" }}>
        {/* Back + logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          {schoolId && (
            <button onClick={() => navigate(`/onboarding/${schoolId}/`)} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: "#90A4AE", fontSize: 14, display: "flex", alignItems: "center", gap: 4, fontFamily: "'Baikal Book', sans-serif" }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Back
            </button>
          )}
          <div style={{ flex: 1 }} />
          {school?.image && <img src={school.image} alt={school.name} style={{ width: 32, height: 32, borderRadius: 8, objectFit: "contain" }} />}
        </div>

        <h1 style={{ fontFamily: "'Baikal Condensed', sans-serif", fontSize: 36, fontWeight: 900, marginBottom: 8 }}>Fan Access Selection</h1>
        <p style={{ fontFamily: "'Baikal Book', sans-serif", fontSize: 14, fontWeight: 300, opacity: 0.5, marginBottom: 24 }}>{school?.name ? `${school.name} — Experience menu` : "Experience menu"}</p>

        <div style={{ fontSize: 14, color: "#546E7A", lineHeight: 1.6, marginBottom: 24, padding: "16px 20px", background: "#F5F7FA", borderRadius: 12 }}>
          Go through each experience and mark <b style={{ color: "#263238" }}>Yes</b>, <b style={{ color: "#263238" }}>Maybe</b>, or <b style={{ color: "#263238" }}>No</b>. There are no wrong answers — this helps us build a marketplace around what works for your program. You can always add more later.
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[["w", "WIN IT \u2014 Raffle"], ["e", "EARN IT \u2014 Leaderboard"], ["b", "BUY IT \u2014 Purchase"]].map(([k, label]) => (
            <span key={k} style={{ fontSize: 11, fontWeight: 500, padding: "4px 12px", borderRadius: 100, background: TAG_COLORS[k].bg, color: TAG_COLORS[k].color }}>{label}</span>
          ))}
        </div>

        {sections.map((sec, si) => {
          let secYes = 0, secMaybe = 0;
          sec.items.forEach(item => { const v = votes[item.id]; if (v === "yes") secYes++; if (v === "maybe") secMaybe++; });
          return (
            <div key={si} style={{ marginBottom: 28 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderRadius: 12, background: SEC_COLORS[sec.cls], marginBottom: 10 }}>
                <span style={{ flex: 1, fontSize: 14, fontWeight: 500, color: "#fff" }}>{sec.title}</span>
                <span style={{ fontSize: 11, color: "rgba(255,255,255,0.7)", whiteSpace: "nowrap" }}>
                  {sec.items.length} options{(secYes > 0 || secMaybe > 0) && ` \u00b7 ${secYes} yes, ${secMaybe} maybe`}
                </span>
              </div>

              <div
                onDragOver={isSuperuser ? (e) => handleSectionDragOver(si, e) : undefined}
                onDrop={isSuperuser ? (e) => handleDrop(e) : undefined}
              >
              {sec.items.map((item, ii) => {
                const key = item.id;
                const v = votes[key] || "";
                const isEditing = editingItem && editingItem.si === si && editingItem.ii === ii;
                const isDraggedItem = dragSource && dragSource.si === si && dragSource.ii === ii;
                const showPlaceholderBefore = dragSource && dropTarget && dropTarget.si === si && dropTarget.ii === ii && !(dragSource.si === si && dragSource.ii === ii) && !(dragSource.si === si && dragSource.ii + 1 === ii);

                return (
                  <div key={key}>
                    {showPlaceholderBefore && (
                      <div style={{ marginBottom: 6, padding: "14px 16px", border: "2px dashed #00B8D4", borderRadius: 12, background: "rgba(0,184,212,0.05)", fontSize: 13, color: "#00B8D4", textAlign: "center" }}>
                        {sections[dragSource.si]?.items[dragSource.ii]?.name}
                      </div>
                    )}
                  <div
                    ref={el => { itemRefsMap.current[`${si}-${ii}`] = el; }}
                    draggable={isSuperuser}
                    onDragStart={isSuperuser ? (e) => handleDragStart(si, ii, e) : undefined}
                    onDragEnd={isSuperuser ? handleDragEnd : undefined}
                    style={{ display: "flex", alignItems: "stretch", marginBottom: 6, border: v === "yes" ? "1.5px solid #4CAF50" : v === "maybe" ? "1.5px solid #FF9800" : "1px solid #E1E8ED", borderRadius: 12, overflow: "hidden", opacity: isDraggedItem ? 0.3 : v === "no" ? 0.45 : 1, transition: "opacity 0.15s", cursor: isSuperuser ? "grab" : "default" }}>
                    <div style={{ flex: 1, padding: "12px 16px" }}>
                      {isEditing ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          <input value={editData.name} onChange={e => setEditData(p => ({ ...p, name: e.target.value }))} placeholder="Name" style={inputStyle} />
                          <input value={editData.desc} onChange={e => setEditData(p => ({ ...p, desc: e.target.value }))} placeholder="Description" style={inputStyle} />
                          <input value={editData.ops} onChange={e => setEditData(p => ({ ...p, ops: e.target.value }))} placeholder="Ops notes" style={inputStyle} />
                          <div style={{ display: "flex", gap: 6 }}>
                            <button onClick={saveEdit} style={{ background: "#00B8D4", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "'Baikal Book', sans-serif" }}>Save</button>
                            <button onClick={() => setEditingItem(null)} style={{ background: "none", border: "none", color: "#90A4AE", fontSize: 12, cursor: "pointer", fontFamily: "'Baikal Book', sans-serif" }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div style={{ fontSize: 14, fontWeight: 500, color: "#263238", marginBottom: 3 }}>{item.name}</div>
                          <div style={{ fontSize: 12, color: "#546E7A", lineHeight: 1.4, marginBottom: 6 }}>{item.desc}</div>
                          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 4, alignItems: "center" }}>
                            {isSuperuser ? ALL_TAGS.map(t => {
                              const active = item.tags.includes(t);
                              return (
                                <span key={t} onClick={() => toggleTag(si, ii, t)} style={{ fontSize: 9, fontWeight: 500, padding: "2px 8px", borderRadius: 100, background: active ? TAG_COLORS[t].bg : "transparent", color: active ? TAG_COLORS[t].color : "#ccc", border: active ? "none" : "1px dashed #ccc", cursor: "pointer" }}>{TAG_MAP[t]}</span>
                              );
                            }) : item.tags.map(t => (
                              <span key={t} style={{ fontSize: 9, fontWeight: 500, padding: "2px 8px", borderRadius: 100, background: TAG_COLORS[t].bg, color: TAG_COLORS[t].color }}>{TAG_MAP[t]}</span>
                            ))}
                          </div>
                          <div style={{ fontSize: 11, color: "#90A4AE", fontStyle: "italic" }}>{item.ops}</div>
                          {/* Admin edit/delete */}
                          {isSuperuser && (
                            <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
                              <button onClick={() => { setEditingItem({ si, ii }); setEditData({ name: item.name, desc: item.desc, ops: item.ops }); }} style={{ background: "none", border: "none", padding: 0, color: "#90A4AE", fontSize: 10, cursor: "pointer", fontFamily: "'Baikal Book', sans-serif", display: "flex", alignItems: "center", gap: 3 }}>
                                <svg width="10" height="10" viewBox="0 0 14 14" fill="none"><path d="M10.08 1.92a1.5 1.5 0 012.12 2.12L5.13 11.1 2 12l.9-3.13L10.08 1.92z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                Edit
                              </button>
                              <button onClick={() => deleteItem(si, ii)} style={{ background: "none", border: "none", padding: 0, color: "#F44336", fontSize: 10, cursor: "pointer", fontFamily: "'Baikal Book', sans-serif" }}>Remove</button>
                            </div>
                          )}
                          {/* Notes (all users) */}
                          {noteOpen === key ? (
                            <div style={{ marginTop: 6, display: "flex", gap: 6, alignItems: "center" }}>
                              <input autoFocus value={notes[key] || ""} onChange={e => setNotes(prev => ({ ...prev, [key]: e.target.value }))} onKeyDown={e => { if (e.key === "Enter" || e.key === "Escape") setNoteOpen(null); }} placeholder="Add a note..." style={{ flex: 1, padding: "6px 10px", border: "1px solid #E1E8ED", borderRadius: 8, fontSize: 11, fontFamily: "'Baikal Book', sans-serif", outline: "none" }} />
                              <button onClick={() => setNoteOpen(null)} style={{ background: "none", border: "none", color: "#90A4AE", fontSize: 11, cursor: "pointer", fontFamily: "'Baikal Book', sans-serif" }}>Done</button>
                            </div>
                          ) : notes[key] ? (
                            <div onClick={() => setNoteOpen(key)} style={{ marginTop: 6, fontSize: 11, color: "#546E7A", background: "#F5F7FA", padding: "6px 10px", borderRadius: 8, cursor: "pointer", lineHeight: 1.4 }}>
                              <span style={{ fontWeight: 500, color: "#263238" }}>Note:</span> {notes[key]}
                            </div>
                          ) : (
                            <div style={{ marginTop: 6, display: "flex", justifyContent: "flex-end" }}>
                              <button onClick={() => setNoteOpen(key)} style={{ background: "none", border: "none", padding: 0, color: "#90A4AE", fontSize: 10, cursor: "pointer", fontFamily: "'Baikal Book', sans-serif", display: "flex", alignItems: "center", gap: 3 }}>
                                <svg width="10" height="10" viewBox="0 0 14 14" fill="none"><path d="M10.08 1.92a1.5 1.5 0 012.12 2.12L5.13 11.1 2 12l.9-3.13L10.08 1.92z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                Add note
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: 4, padding: "8px 12px", background: "#F5F7FA", borderLeft: "1px solid #E1E8ED", minWidth: 90 }}>
                      {["yes", "maybe", "no"].map(opt => {
                        const active = v === opt;
                        const btnStyle = {
                          display: "flex", alignItems: "center", gap: 6, padding: "6px 10px",
                          borderRadius: 8, border: "1px solid",
                          borderColor: active ? (opt === "yes" ? "#4CAF50" : opt === "maybe" ? "#FF9800" : "#F44336") : "#E1E8ED",
                          background: active ? (opt === "yes" ? "rgba(76,175,80,0.1)" : opt === "maybe" ? "rgba(255,152,0,0.1)" : "rgba(244,67,54,0.1)") : "#fff",
                          color: active ? (opt === "yes" ? "#2E7D32" : opt === "maybe" ? "#E65100" : "#C62828") : "#546E7A",
                          cursor: "pointer", fontSize: 11, fontWeight: 500, width: "100%",
                          fontFamily: "'Baikal Book', sans-serif", transition: "all 0.12s",
                        };
                        return (
                          <div key={opt} style={btnStyle} onClick={() => vote(key, opt)}>
                            <span style={{ width: 10, height: 10, borderRadius: "50%", border: active ? "none" : "1.5px solid #90A4AE", background: active ? btnStyle.borderColor : "transparent", flexShrink: 0 }} />
                            {opt.charAt(0).toUpperCase() + opt.slice(1)}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  </div>
                );
              })}

              {/* End-of-section placeholder */}
              {dragSource && dropTarget && dropTarget.si === si && dropTarget.ii === sec.items.length && !(dragSource.si === si && (dragSource.ii === sec.items.length - 1 || dragSource.ii === sec.items.length)) && (
                <div style={{ marginBottom: 6, padding: "14px 16px", border: "2px dashed #00B8D4", borderRadius: 12, background: "rgba(0,184,212,0.05)", fontSize: 13, color: "#00B8D4", textAlign: "center" }}>
                  {sections[dragSource.si]?.items[dragSource.ii]?.name}
                </div>
              )}
              </div>

              {/* Admin: add item form */}
              {isSuperuser && addingTo === si && (
                <div style={{ padding: "12px 16px", border: "1px dashed #E1E8ED", borderRadius: 12, marginBottom: 6, display: "flex", flexDirection: "column", gap: 6 }}>
                  <input value={newItem.name} onChange={e => setNewItem(p => ({ ...p, name: e.target.value }))} placeholder="Experience name" style={inputStyle} />
                  <input value={newItem.desc} onChange={e => setNewItem(p => ({ ...p, desc: e.target.value }))} placeholder="Description" style={inputStyle} />
                  <input value={newItem.ops} onChange={e => setNewItem(p => ({ ...p, ops: e.target.value }))} placeholder="Ops notes" style={inputStyle} />
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => addItem(si)} style={{ background: "#00B8D4", color: "#fff", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "'Baikal Book', sans-serif" }}>Add</button>
                    <button onClick={() => { setAddingTo(null); setNewItem({ name: "", desc: "", ops: "" }); }} style={{ background: "none", border: "none", color: "#90A4AE", fontSize: 12, cursor: "pointer", fontFamily: "'Baikal Book', sans-serif" }}>Cancel</button>
                  </div>
                </div>
              )}
              {isSuperuser && addingTo !== si && (
                <button onClick={() => { setAddingTo(si); setNewItem({ name: "", desc: "", ops: "" }); }} style={{ background: "none", border: "1px dashed #E1E8ED", borderRadius: 12, padding: "10px 16px", width: "100%", color: "#90A4AE", fontSize: 12, cursor: "pointer", fontFamily: "'Baikal Book', sans-serif", marginBottom: 6 }}>
                  + Add experience
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer counts */}
      <div style={{ position: "sticky", bottom: 0, background: "#fff", borderTop: "1px solid #E1E8ED", zIndex: 10 }}>
        <div style={{ maxWidth: 860, margin: "0 auto", padding: "12px 24px", display: "flex", justifyContent: "flex-end", gap: 20 }}>
          {[
            { n: yesCount, label: "Yes", color: "#4CAF50" },
            { n: maybeCount, label: "Maybe", color: "#FF9800" },
            { n: noCount, label: "No", color: "#F44336" },
            { n: total - yesCount - maybeCount - noCount, label: "Remaining", color: "#90A4AE" },
          ].map(s => (
            <div key={s.label} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 500, color: s.color }}>{s.n}</div>
              <div style={{ fontSize: 10, color: "#90A4AE" }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
